import { METHOD_IDS } from '@/components/admin/reglages/method-contracts';
import { METHOD_QUERY_KEY, MethodEditor } from '@/components/admin/reglages/method-editor';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { fetchMethod, methodApi } from '@/lib/api';
import {
  METHOD_NOT_BORN_ATELIER,
  METHOD_NOT_BORN_VITRINE,
  METHOD_READ_FAILED,
} from '@/lib/method-labels';
import { useIsVitrine } from '@/lib/use-instance';
import { METHOD_ID } from '@matter_extractor/shared';
import { Alert, Spinner } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * Réglages > Méthode (analyste.md §4.4, issue #196): the EIGHTH tab of Réglages
 * when the replay is open, the seventh of every server - after Noyaux (K12) - and
 * the editor of « Méthode », the words of the three functions and the six styles
 * the analyst of the noyau reads in its two passes.
 *
 * One versioned object, a singleton on the layer of the texts of the noyaux, the
 * models and the rules: no list to open it from, so the tab IS its editor. It is
 * born v1 of a seed at the start of the atelier, so its detail never answers 404
 * there; a vitrine receives it by « Passer en production » and answers 404 until
 * then - which is a state of this page (`fetchMethod` reads `null`) and not an
 * error. The tab reads, on the vitrine, as the other screens of a model do: the
 * same editor, with nothing typable and no gesture in its head (vitrine.md §7).
 *
 * ## No title of its own
 *
 * The planche draws none: the head of the layer - « Méthode », its pointers, its
 * history - is the first thing under the bar, and the sentence that says what each
 * pass reads comes right after it.
 *
 * ## « Voir » travels in the URL
 *
 * `?version=<id>` opens a version read-only, the régime of every other editor and
 * for its reason: a version somebody is reading is what gets pasted into a thread.
 * An unknown parameter degrades to « no version ».
 *
 * ## The editor's key is a counter
 *
 * The régime of `reglages.regles.tsx`: the editor seeds its draft from the detail
 * ONCE, so a reload of a stale draft has to be a REMOUNT, and a key carrying the
 * draft's stamp would remount under the caret at every autosave.
 */

type MethodSearch = { version?: string };

function readSearch(search: Record<string, unknown>): MethodSearch {
  const raw = search.version;
  return typeof raw === 'string' && raw !== '' ? { version: raw } : {};
}

export const Route = createFileRoute('/admin/reglages/methode')({
  validateSearch: readSearch,
  component: ReglagesMethodeRoute,
});

function ReglagesMethodeRoute() {
  const { version } = Route.useSearch();
  const navigate = useNavigate();
  return (
    <ReglagesMethodePage
      version={version ?? null}
      onView={(versionId) =>
        void navigate({ to: '/admin/reglages/methode', search: { version: versionId } })
      }
      onCloseView={() => void navigate({ to: '/admin/reglages/methode', search: {} })}
    />
  );
}

export interface ReglagesMethodePageProps {
  /** The version « Voir » opened, from `?version=`, or null. */
  readonly version: string | null;
  readonly onView: (versionId: string) => void;
  readonly onCloseView: () => void;
}

/**
 * EXPORTED for `tests/reglages-methode.test.tsx`: `vitest.config.ts` leaves
 * `src/routes/**` out of the coverage REPORT, not out of the suite, and what this
 * page decides - the 404 read as « not born », the key of the editor, the
 * read-only view - is invisible from the components below it.
 */
export function ReglagesMethodePage({ version, onView, onCloseView }: ReglagesMethodePageProps) {
  const { email } = useAdminSession();
  const vitrine = useIsVitrine();
  /** Bumped by « Recharger le brouillon du serveur », and by nothing else. */
  const [reload, setReload] = useState(0);

  const settings = useQuery({
    queryKey: [METHOD_QUERY_KEY, email, METHOD_ID],
    queryFn: fetchMethod,
    retry: false,
  });

  const viewed = useQuery({
    queryKey: ['method-version', email, version],
    queryFn: () => methodApi.readVersion(METHOD_ID, version ?? ''),
    // Nothing to read before Méthode is born: no version id can be real yet.
    enabled: version !== null && settings.data !== undefined && settings.data !== null,
    retry: false,
  });

  // A 401 is the door reopening, not an error of this screen (T6).
  const rejected = useRejectOn401(settings.error);

  return (
    <div className="space-y-6 pb-16">
      {settings.isPending && !settings.isError ? (
        <div className="flex justify-center py-12">
          <Spinner label="Chargement de Méthode" />
        </div>
      ) : null}

      {settings.isError && !rejected ? (
        <p role="alert" className="text-rouge-deep text-small">
          {METHOD_READ_FAILED}
        </p>
      ) : null}

      {settings.data === null ? (
        <div data-testid={METHOD_IDS.notBorn}>
          <Alert tone="info" title="Aucune version.">
            {vitrine ? METHOD_NOT_BORN_VITRINE : METHOD_NOT_BORN_ATELIER}
          </Alert>
        </div>
      ) : null}

      {settings.data === undefined || settings.data === null ? null : (
        <MethodEditor
          key={`methode-${reload}`}
          detail={settings.data}
          viewing={viewed.data ?? null}
          onView={(summary) => onView(summary.id)}
          onCloseView={onCloseView}
          onReload={() => {
            // The bump waits for the answer: a remount before it lands would
            // re-seed the editor from the stale draft it is escaping.
            void settings.refetch().then(() => setReload((was) => was + 1));
          }}
        />
      )}
    </div>
  );
}
